import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import Seo, { absUrl } from "@/components/Seo";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/auth/AuthContext";
import { SiteDataProvider, useSiteData } from "@/context/SiteData";
import { BookingProvider } from "@/context/BookingContext";
import { Navbar } from "@/components/landing/Navbar";
import { Hero } from "@/components/landing/Hero";
import { GroupBooking } from "@/components/landing/GroupBooking";
import { Marquee } from "@/components/landing/Marquee";
import { Tours } from "@/components/landing/Tours";
import { Services } from "@/components/landing/Services";
import { Destinations } from "@/components/landing/Destinations";
import { WhyChoose } from "@/components/landing/WhyChoose";
import { About } from "@/components/landing/About";
import { Contact } from "@/components/landing/Contact";
import { ClosingNote } from "@/components/landing/ClosingNote";
import { SeasonalTopbar, SeasonalBanner, SeasonalPopup, SeasonalEffects, ThemePageBackground } from "@/components/landing/SeasonalTheme";
import { SeasonalAnimation } from "@/components/landing/SeasonalAnimation";
import { SpecialOffers } from "@/components/landing/SpecialOffers";
import { Footer } from "@/components/landing/Footer";
import { LoginPage } from "@/pages/LoginPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { AdminPage } from "@/pages/AdminPage";
import { AdminTicketPage } from "@/pages/AdminTicketPage";
import { MyReservations } from "@/pages/MyReservations";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { TourDetailPage } from "@/pages/TourDetailPage";
import { SeoLandingPage } from "@/pages/SeoLandingPage";
import { BlogPage } from "@/pages/BlogPage";
import { BlogPostPage } from "@/pages/BlogPostPage";
import { FleetDetailPage } from "@/pages/FleetDetailPage";
import { Analytics } from "@/components/Analytics";
import { ChatWidget } from "@/components/ChatWidget";
import { SEO_PAGES, TRANSLATED_SLUGS } from "@/data/seoPages";
import { HOME_SEO } from "@/data/i18n";

const Landing = () => {
  const { settings, content, locale, siteDesign } = useSiteData();
  const homeSeo = HOME_SEO[locale] || HOME_SEO.en;
  const sec = (key) => siteDesign?.[key] || {};
  const hidden = (key) => !!sec(key).hidden;
  const secStyle = (key) => {
    const s = sec(key);
    const pad = { "-2": "py-6", "-1": "py-10", 0: "", 1: "py-24", 2: "py-32" }[s.padding ?? 0];
    return {
      className: pad,
      style: {
        ...(s.bg_color ? { background: s.bg_color } : {}),
        ...(s.text_color ? { color: s.text_color } : {}),
        ...(s.images?.bg ? { backgroundImage: `url(${s.images.bg})`, backgroundSize: "cover", backgroundPosition: "center" } : {}),
        ...(s.accent_color ? { "--brand": s.accent_color, "--section-accent": s.accent_color } : {}),
      },
    };
  };
  return (
    <>
      <Seo
        title={homeSeo.title || settings.seo_title}
        siteName="Voyage Switzerland"
        description={homeSeo.description || settings.seo_description}
        image="/api/og-image"
        alternates={homeSeo.alternates}
        lang={homeSeo.lang}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "TravelAgency",
              name: "Voyage Switzerland",
              url: window.location.origin,
              logo: absUrl("/logo.png"),
              image: absUrl("/og-image.png"),
              description: settings.seo_description,
              telephone: content.contact_phone,
              email: content.contact_email,
              sameAs: ["https://www.instagram.com/voyageswisscom/"],
              address: {
                "@type": "PostalAddress",
                streetAddress: "Rieterstrasse 6",
                postalCode: "8002",
                addressLocality: "Zürich",
                addressCountry: "CH",
              },
            },
            {
              "@type": "WebSite",
              name: "Voyage Switzerland",
              url: window.location.origin,
            },
          ],
        }}
      />
      <Navbar />
      <ThemePageBackground />
      <main>
        <div id="home" className={settings.promo_enabled && settings.promo_text ? "h-[118px]" : "h-[76px]"} />
        <SeasonalTopbar />
        <Marquee />
        {!hidden("hero") && <div {...secStyle("hero")}><Hero /></div>}
        <SeasonalBanner page="home" />
        {!hidden("tours") && <div {...secStyle("tours")}><Tours /></div>}
        <GroupBooking />
        {!hidden("destinations") && <div {...secStyle("destinations")}><Destinations /></div>}
        {!hidden("services") && <div {...secStyle("services")}><Services /></div>}
        <WhyChoose />
        {!hidden("about") && <div {...secStyle("about")}><About /></div>}
        {!hidden("contact") && <div {...secStyle("contact")}><Contact /></div>}
        <ClosingNote />
        <SeasonalPopup />
        <SeasonalEffects />
      </main>
      {!hidden("footer") && <div {...secStyle("footer")}><Footer /></div>}
    </>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteDataProvider>
          <BookingProvider>
            <div className="min-h-screen bg-white font-sans text-[#0b132b] antialiased">
              <Analytics />
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/de" element={<Landing />} />
                <Route path="/fr" element={<Landing />} />
                <Route path="/ar" element={<Landing />} />
                <Route path="/tours/:slug" element={<TourDetailPage />} />
                <Route path="/fleet/:slug" element={<FleetDetailPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                {SEO_PAGES.map((p) => (
                  <Route key={p.slug} path={`/${p.slug}`} element={<SeoLandingPage lang="en" slug={p.slug} />} />
                ))}
                {TRANSLATED_SLUGS.flatMap((slug) => [
                  <Route key={`de-${slug}`} path={`/de/${slug}`} element={<SeoLandingPage lang="de" slug={slug} />} />,
                  <Route key={`fr-${slug}`} path={`/fr/${slug}`} element={<SeoLandingPage lang="fr" slug={slug} />} />,
                  <Route key={`ar-${slug}`} path={`/ar/${slug}`} element={<SeoLandingPage lang="ar" slug={slug} />} />,
                ])}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/admin/ticket/:id" element={<AdminTicketPage />} />
                <Route path="/my-reservations" element={<MyReservations />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              <ChatWidget />
              <SeasonalAnimation />
              <SpecialOffers />
              <Toaster position="top-center" richColors />
            </div>
          </BookingProvider>
        </SiteDataProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
